import { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "../../firebase/init";
import { httpsGetFirebaseToken } from "../../firebase/https";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAppSubscription, getSubscriptionHasInit } from "../../redux/slices/app";
import {
  getAppHasAuthenticated,
  getIsEmulating,
  setHasAuthenticated,
  setIsAdmin,
  setUser,
  getRequestShop
} from "../../redux/slices/env";
import { paths } from "@/shared/refs/paths";
import type { FBIdTokenResultsClaims } from "@/shared/typings/types/firebase";
import { signInWithCustomToken, signOut, onAuthStateChanged, IdTokenResult, setPersistence, browserSessionPersistence, inMemoryPersistence } from "firebase/auth";
import { hasSessionStorage } from "@/app/utils/helpers";

type Props = {
  children: React.ReactNode;
};

export default function Authenticator({ children }: Props) {
  const [loading, setLoading] = useState(true);
  const authAttempts = useRef(0);
  const MAX_AUTH_ATTEMPTS = 3;
  const hasAuthenticated = useAppSelector(getAppHasAuthenticated);
  const isEmulating = useAppSelector(getIsEmulating);
  const subscription = useAppSelector(getAppSubscription);
  const subscriptionHasInit = useAppSelector(getSubscriptionHasInit);
  const dispatch = useAppDispatch();

  const appHandle = import.meta.env.VITE_APP_HANDLE || "handbook";
  const shop = useAppSelector(getRequestShop); // Get request shop (handles emulating)

  const searchParams = new URLSearchParams(window.location.search);
  const planHandle = searchParams.get("plan_handle");

  // Keep Shopify App Bridge loading indicator in sync
  useEffect(() => {
    shopify.loading(loading || !hasAuthenticated);
  }, [loading, hasAuthenticated]);

  const signinError = useCallback(() => {
    setLoading(false);
    dispatch(setHasAuthenticated(false));
    shopify.toast.show("Unexpected authentication error", {
      isError: true,
      duration: 10000,
      // action: "Contact Support"
      // onAction
    });
  }, []);

  const authenticate = useCallback(async () => {
    if (authAttempts.current >= MAX_AUTH_ATTEMPTS || !shop) {
      signinError();
      return;
    }

    try {
      authAttempts.current += 1;

      // Fetch fresh session token from Shopify App Bridge v4
      const sessionToken = await shopify.idToken();

      // Request Firebase Custom Token from backend
      const firebaseToken = await httpsGetFirebaseToken({ sessionToken, shop });

      await setPersistence(auth, hasSessionStorage() ? browserSessionPersistence : inMemoryPersistence)

      // Sign into Firebase Auth
      const userCredential = await signInWithCustomToken(auth, firebaseToken);
      const token = await userCredential.user.getIdTokenResult();

      return token;
    } catch (error) {
      console.error("Authentication attempt failed:", error);
      signinError();
    }
  }, [shop, signinError]);

  const handleToken = useCallback(async (token?: IdTokenResult) => {
    if (!token) return;

    const claims = token.claims as FBIdTokenResultsClaims;
    const shopMatches = Boolean(claims.shop && claims.shop === shop);
    if (shopMatches) {
      dispatch(setUser(claims.user));
      dispatch(setIsAdmin(!!claims.admin));
      dispatch(setHasAuthenticated(true));
      setLoading(false);
    } else {
      signOut(auth);
      authenticate();
    }
  }, [shop, dispatch, authenticate]);

  // Monitor auth state changes
  useEffect(() => {
    const authWatch = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdTokenResult();
        await handleToken(token);
      } else {
        await authenticate();
      }
    });
    return authWatch;
  }, [handleToken, authenticate]);

  // Check subscription billing state
  const checkSubscription = useCallback(() => {
    if (!shop || isEmulating) return;

    if (subscriptionHasInit && !subscription?.appPlanActive && !planHandle) {
      const redirectUrl = paths.appPlanSelectionPage(appHandle, shop);
      open(redirectUrl, "_top");
    }
  }, [shop, subscriptionHasInit, subscription, planHandle, isEmulating, appHandle]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  if (!hasAuthenticated) return null;

  return <>{children}</>;
}
